// statisticsReport.js
// -------------------
// PURE aggregation for the "Statistics Summary" Standard Report PDF. It reproduces
// the numbers on the Statistics dashboard (Approved / committed spend only), scoped
// by the Standard Reports filter (From/To business day + State), so the printed
// report matches the on-screen dashboard.
//
// ⚠️ CROSS-REFERENCE: this MIRRORS the inline aggregation in
// src/components/dashboard/StatisticsPage.jsx. It was duplicated rather than extracted
// so the live dashboard is untouched (same "duplicate + cross-reference, don't refactor
// live code" call used elsewhere in this codebase). If the dashboard's category /
// state / trend / shelter math changes, update BOTH. Response Time is NOT computed
// here (it needs the responder-task list); the caller gathers it separately.

import { REQUEST_STATUSES } from "../utils/enums";
import { calcUniItems } from "../utils/formConstants";
import { decisionMatch, isStationeryFee, requestDecisionTotals } from "../utils/helpers";

// Fixed top-down order for the Requests-by-Status panel (mirrors StatisticsPage).
export const STAT_STATUS_ORDER = [
  REQUEST_STATUSES.DRAFT,
  REQUEST_STATUSES.SUBMITTED,
  REQUEST_STATUSES.PART_PROCESSED,
  REQUEST_STATUSES.FULLY_PROCESSED,
];

// By-category rows, in display order. "laptops" shows as "Devices"; Stationery is
// split out of Fees (a stationery fee type — isStationeryFee — counts as Stationery).
export const STAT_CATEGORIES = [
  { key: "stationery", label: "Stationery" },
  { key: "fees", label: "Fees" },
  { key: "uniforms", label: "Uniforms" },
  { key: "shoes", label: "Shoes" },
  { key: "laptops", label: "Devices" },
];

// Build the whole report from the shared request list. Approved-only.
// opts: { fromDate, toDate, state } — the Standard Reports filter (state "All" = any).
export function buildStatisticsReport(requests, opts = {}) {
  const { fromDate = "", toDate = "", state = "All" } = opts;

  // Scope by the Brisbane business day (reportDay), same as the dashboard, + State.
  const filtered = (requests || []).filter((r) => {
    const rday = r.reportDay || r.date || "";
    if (fromDate && rday < fromDate) return false;
    if (toDate && rday > toDate) return false;
    if (state && state !== "All" && r.state !== state) return false;
    return true;
  });

  const totalReqs = filtered.length;
  const processedReqs = filtered.filter(
    (r) => r.status === REQUEST_STATUSES.FULLY_PROCESSED
  ).length;

  // Approved-only per-request totals (fees EXCLUDE stationery — see requestDecisionTotals).
  const agg = filtered.map((r) => requestDecisionTotals(r, "approved"));
  const approvedValue = agg.reduce((s, a) => s + a.total, 0);
  const approvedChildren = agg.reduce((s, a) => s + a.children, 0);
  const approvedReqs = agg.filter((a) => a.hasApproved).length;
  const feesTotal = agg.reduce((s, a) => s + a.fees, 0);
  const uniformsTotal = agg.reduce((s, a) => s + a.uniforms, 0);
  const shoesTotal = agg.reduce((s, a) => s + a.shoes, 0);
  const devicesTotal = agg.reduce((s, a) => s + a.laptops, 0);
  const stationeryTotal = agg.reduce((s, a) => s + a.stationery, 0);

  // By category — Stationery split out of Fees (mirrors the dashboard's catStats).
  const cat = {
    stationery: { count: 0, children: 0, value: 0 },
    fees: { count: 0, children: 0, value: 0 },
    uniforms: { count: 0, children: 0, value: 0 },
    shoes: { count: 0, children: 0, value: 0 },
    laptops: { count: 0, children: 0, value: 0 },
  };
  filtered.forEach((r) => {
    const inReq = { fees: false, uniforms: false, shoes: false, laptops: false, stationery: false };
    (r.children || []).forEach((c) => {
      let childFees = false;
      let childStationery = false;
      (c.fees || []).forEach((f) => {
        if (!decisionMatch(f.approvalStatus, "approved")) return;
        const amt = parseFloat(f.feeAmount) || 0;
        if (isStationeryFee(f.feeType)) {
          cat.stationery.value += amt;
          childStationery = true;
        } else {
          cat.fees.value += amt;
          childFees = true;
        }
      });
      if (childFees) {
        cat.fees.children++;
        inReq.fees = true;
      }
      if (childStationery) {
        cat.stationery.children++;
        inReq.stationery = true;
      }
      if (c.uniforms && decisionMatch(c.uniforms.approvalStatus, "approved")) {
        cat.uniforms.value += calcUniItems([c.uniforms]);
        cat.uniforms.children++;
        inReq.uniforms = true;
      }
      if (c.shoes && decisionMatch(c.shoes.approvalStatus, "approved")) {
        cat.shoes.value +=
          (c.shoes.jQty || 0) * (parseFloat(c.shoes.jCost) || 0) +
          (c.shoes.lQty || 0) * (parseFloat(c.shoes.lCost) || 0);
        cat.shoes.children++;
        inReq.shoes = true;
      }
      if (c.laptop && decisionMatch(c.laptop.approvalStatus, "approved")) {
        cat.laptops.value += parseFloat(c.laptop.estimatedCost) || 0;
        cat.laptops.children++;
        inReq.laptops = true;
      }
    });
    for (const k of ["fees", "uniforms", "shoes", "laptops", "stationery"]) {
      if (inReq[k]) cat[k].count++;
    }
  });
  const byCategory = STAT_CATEGORIES.map((c) => ({ key: c.key, label: c.label, ...cat[c.key] }));

  // By state — approved-only; states with no approved items are omitted.
  const stateMap = {};
  filtered.forEach((r) => {
    const a = requestDecisionTotals(r, "approved");
    if (!a.hasApproved) return;
    if (!stateMap[r.state]) stateMap[r.state] = { count: 0, children: 0, value: 0 };
    stateMap[r.state].count++;
    stateMap[r.state].children += a.children;
    stateMap[r.state].value += a.total;
  });
  const byState = Object.entries(stateMap)
    .map(([st, s]) => ({ state: st, ...s }))
    .sort((a, b) => b.value - a.value);

  // By status — ordered, with any unknown statuses appended.
  const statusMap = {};
  filtered.forEach((r) => {
    statusMap[r.status] = (statusMap[r.status] || 0) + 1;
  });
  const byStatus = [
    ...STAT_STATUS_ORDER.filter((st) => statusMap[st] != null).map((st) => ({
      status: st,
      count: statusMap[st],
    })),
    ...Object.keys(statusMap)
      .filter((st) => !STAT_STATUS_ORDER.includes(st))
      .map((st) => ({ status: st, count: statusMap[st] })),
  ];

  // Monthly trend — approved committed spend, grouped by month (YYYY-MM).
  const trendMap = {};
  filtered.forEach((r) => {
    const key = (r.reportDay || r.date || "unknown").slice(0, 7);
    if (!trendMap[key]) trendMap[key] = { count: 0, children: 0, value: 0 };
    const a = requestDecisionTotals(r, "approved");
    if (!a.hasApproved) return;
    trendMap[key].count++;
    trendMap[key].value += a.total;
    trendMap[key].children += a.children;
  });
  const trend = Object.keys(trendMap)
    .sort()
    .map((k) => ({ key: k, ...trendMap[k] }));

  // By DFV Service — approved-only, per-category $ + total (stationery has its own figure,
  // not in fees, so the $ columns add up to the total).
  const shMap = {};
  filtered.forEach((r) => {
    const a = requestDecisionTotals(r, "approved");
    if (!a.hasApproved) return;
    if (!shMap[r.shelterName]) {
      shMap[r.shelterName] = {
        state: r.state,
        requests: 0,
        children: 0,
        fees: 0,
        uniforms: 0,
        shoes: 0,
        devices: 0,
        stationery: 0,
        value: 0,
      };
    }
    const m = shMap[r.shelterName];
    m.requests++;
    m.children += a.children;
    m.fees += a.fees;
    m.uniforms += a.uniforms;
    m.shoes += a.shoes;
    m.devices += a.laptops;
    m.stationery += a.stationery;
    m.value += a.total;
  });
  const byShelter = Object.entries(shMap)
    .map(([name, s]) => ({ name, ...s }))
    .sort((a, b) => b.value - a.value);

  return {
    totals: {
      approvedReqs,
      approvedChildren,
      approvedValue,
      processedReqs,
      totalReqs,
      avgChildren: approvedReqs > 0 ? approvedChildren / approvedReqs : 0,
      avgPerReq: approvedReqs > 0 ? approvedValue / approvedReqs : 0,
      avgPerChild: approvedChildren > 0 ? approvedValue / approvedChildren : 0,
      feesTotal,
      uniformsTotal,
      shoesTotal,
      devicesTotal,
      stationeryTotal,
    },
    byStatus,
    byCategory,
    byState,
    trend,
    byShelter,
  };
}
